<template>
<layout-content>
  <template #toolbar>
    <period-time
      :query="query"
      :start-date-picker-options="startDatePickerOptions"
      :end-date-picker-options="endDatePickerOptions"
      :show-options="showOptions"
    >
      <site-query :sites="filterSites" :query="query" />

      <completed-query v-model="query.isAuditCompleted" @change="onAuditCompletedChanged" />

      <el-button
        :disabled="canNotSearch"
        type="orange"
        size="small"
        icon="el-icon-search"
        @click="onSearchBtnClick"
      >查询</el-button>
    </period-time>
  </template>

  <template #body>
    <data-wrapper>
      <template #header>
        <el-checkbox
          border
          size="small"
          label="显示饼图"
          v-model="isShowPie"
          style="margin-left: .5em;"
        />
      </template>

      <x-y-pie-chart
        v-for="(data, index) in dataList"
        :key="data.id"
        :index="index"
        :data-config="data"
        :unit-prop="unitProp"
        :chart-type="chartType"
        :stack-type="stackType"
        :is-show-pie="isShowPie"
        :download-name="downloadName"
        :pie-dimension-ids="pieDimensionIds"
        chart-min-height="400px"
        chart-height="auto"
        :data-time-format="dateFormatter"
        need-resize
      />
    </data-wrapper>
  </template>
</layout-content>
</template>

<script>
import BaseSiteFactorData from '../../../BaseSiteFactorData'
import DeviceCategoryConfig from '../../../../model/DeviceCategoryConfig'

import LayoutContent from '../../../../components/Layout/Content'
import DataTimeQuery from '../../../../components/Data/Query/DataTimeQuery'
import SiteQuery from '../../../../components/Data/Query/SiteQuery'
import CompletedQuery from '../../../../components/Data/Query/CompletedQuery'
import DataWrapper from '../../../../components/Data/DataWrapper'
import XYChart from '../../../../components/DataChart/XYChart'
import Highcharts from 'highcharts'
import highcharts3d from 'highcharts/highcharts-3d'
import exporting from 'highcharts/modules/exporting'
import XYPieChart from './XYPieChart'
import PeriodTime from '../../../../components/Data/PeriodTime'
import unitConverter from '../../../../utils/unitConverter'

exporting(Highcharts)
highcharts3d(Highcharts)

export default {
  name: 'StatisticalAnalysisPM25BCRatio',
  routeInfo: {
    label: 'PM2.5与BC',
    icon: 'fa fa-eye'
  },
  extends: BaseSiteFactorData,
  components: {PeriodTime, XYPieChart, LayoutContent, DataTimeQuery, SiteQuery, CompletedQuery, DataWrapper, XYChart},
  data: () => ({
    query: {
      factorIds: [],
      isMultipleIntegral: true,
      granularity: 'hour',
      isFuse: false,
      isGetInvalidData: true
    },
    chartType: 'bar',
    stackType: 1,
    isShowPie: true,
    showHighChart: Object.freeze([]),
    dateFormatter: null
  }),
  computed: {
    siteDeviceCategoryConfig: () => [
      new DeviceCategoryConfig(process.env.PM_DEVICE_CATEGORY, true, siteDeviceFactors => siteDeviceFactors.filter(siteDeviceFactor => siteDeviceFactor.factorId === '1008')),
      new DeviceCategoryConfig(process.env.BLACK_CARBON_DEVICE_CATEGORY, true)
    ],
    showOptions () {
      return ['hour', 'day', 'month', 'quarter']
    },
    granularityOptMap: () => ({
      hour: {
        // formatter: 'YY/MM/DD HH',
        formatter: null,
        getDate: dataTime => new Date(`${dataTime}:00:00`).getTime()
      },
      day: {
        // formatter: 'YY/MM/DD',
        formatter: null,
        getDate: dataTime => new Date(`${dataTime} 00:00:00`).getTime()
      },
      month: {
        // formatter: 'YY/MM',
        formatter: null,
        getDate: dataTime => new Date(`${dataTime}/01 00:00:00`).getTime()
      },
      quarter: {
        formatter: null,
        getDate: dataTime => dataTime
      }
    }),
    startDatePickerOptions () {
      return {
        disabledDate: time => time.getTime() > new Date(this.query.endTime)
      }
    },
    endDatePickerOptions () {
      return {
        disabledDate: time => time.getTime() < new Date(this.query.beginTime)
      }
    },
    downloadName: () => 'PM2.5与BC比值',
    pieDimensionIds: () => ['1008', '3507']
  },
  methods: {
    getSeriesParam (dimension, dimensionIds) {
      let factorId = dimensionIds[dimension ^ 1]

      if (factorId === '1008' || factorId === '3507') {
        return {
          type: 'line',
          showSymbol: false
        }
      } else {
        return {
          type: 'bar',
          stack: dimensionIds[dimension],
          barMaxWidth: 30
        }
      }
    },
    setDataList (dataList) {
      this.tmpList = Object.freeze(dataList)
      const {currentQuery, siteFactorMap, filterSiteMap} = this
      const {beginTime, endTime} = currentQuery
      const beginTS = new Date(beginTime).getTime()
      const endTS = new Date(endTime).getTime()
      const granularityOpt = this.granularityOptMap[currentQuery.granularity]
      const noDataReasonMap = Object.values(this.noDataReasonMap).reduce((map, noDataReason) => {
        const {siteId} = noDataReason
        const siteConfig = filterSiteMap[siteId]

        if (siteConfig) {
          const {siteDeviceId} = noDataReason
          const siteDevice = siteConfig.devices.find(siteDevice => siteDevice.id === siteDeviceId)

          if (siteDevice) {
            const dataTime = noDataReason.dataTime

            siteDevice.siteDeviceFactors.forEach(siteDeviceFactor => {
              map[`${siteId}-${siteDeviceFactor.factorId}-${dataTime}`] = noDataReason
            })
          }
        }

        return map
      }, {})

      const formatList = dataList.map(data => {
        const {siteId} = data
        const dataList = data.data.sort((a, b) => a.dataTime.localeCompare(b.dataTime))
        const site = filterSiteMap[siteId]
        const factors = Object.values(siteFactorMap[siteId].map)

        return {
          id: siteId,
          name: site.name,
          beginTS,
          endTS,
          dataTimes: dataList.map(({dataTime}) => [granularityOpt.getDate(dataTime), dataTime]),
          dimensions: factors,
          seriesMap: factors.reduce((map, factor) => {
            const factorId = factor.id
            map[factorId] = dataList.reduce((map, data) => {
              const {dataTime} = data
              const value = data[factorId]

              if (Number.isFinite(value)) {
                map[dataTime] = {
                  dataTime,
                  value
                }
              } else {
                const noDataReason = noDataReasonMap[`${siteId}-${factorId}-${dataTime}`]

                if (noDataReason) {
                  map[dataTime] = {
                    dataTime,
                    noDataReason: noDataReason.reason
                  }
                }
              }

              return map
            }, {})
            return map
          }, {})
        }
      })

      this.dateFormatter = granularityOpt.formatter

      const { factorMap } = this
      const bcStdUnit = factorMap['3507'].stdUnit
      const pm25StdUnit = factorMap['1008'].stdUnit

      this.dataList = Object.freeze(formatList.map(data => {
        const id = '3507-1008'
        const {seriesMap} = data
        const bcSeriesMap = seriesMap['3507']
        const pm25SeriesMap = seriesMap['1008']
        seriesMap[id] = data.dataTimes.reduce((map, [ts, dataTime]) => {
          const bcData = bcSeriesMap[dataTime]

          if (bcData) {
            const pm25Data = pm25SeriesMap[dataTime]

            if (pm25Data) {
              const {value} = pm25Data

              if (value !== 0) {
                map[dataTime] = {
                  ts,
                  // value: (!Number.isFinite(bcData.value) || !Number.isFinite(value)) ? null : Number((bcData.value / value).toFixed(3)),
                  value: (!Number.isFinite(bcData.value) || !Number.isFinite(value)) ? null : Number((bcData.value / unitConverter(value, pm25StdUnit, bcStdUnit)).toFixed(3)),
                  isHaveIntegral: bcData.isHaveIntegral || pm25Data.isHaveIntegral
                }
              }
            }
          }

          return map
        }, {})

        return {
          ...data,
          dimensions: [{
            id,
            name: 'BC/PM2.5',
            unit: '',
            displayUnit: '',
            seriesParam: {
              type: 'line',
              showSymbol: false
            }
          }, ...data.dimensions.sort((a, b) => a.id === '1008' || b.id === '1008' ? 0 : (b.id - a.id))]
        }
      }))
    },
    getData () {
      this.changeLoadStatus(true)

      const {currentQuery} = this

      Promise.all([
        this.$api.Data.getAvgData(currentQuery),
        this.$api.NoData.getNoDataListBySite(currentQuery)
      ]).then(this.setAllDataList).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/00PM/05PM25-BC-Ratio/index.vue